import React, {useEffect} from "react";
import {ActionRow, ActionRowButton, Alert, InputLabel, Modal, TextareaField, Toggle, useNotifications} from "peggirkit";
import {toggleLabel} from "peggirkit/dist/components/Input/Toggle/Single/Label/DefaultToggleLabel";
import {FormattedMessage, useIntl} from "react-intl";
import {useForm} from "react-hook-form";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {TrashIcon} from "@heroicons/react/20/solid";
import {orderCancellationValidation, PlacedOrderOverview, PosOrderCancellation} from "@/data/entities/order";
import {useRestaurant} from "@/context/restaurant/RestaurantProvider";
import {POS_ORDER_QUERY_KEY} from "@/context/pos/UsePosOrder";
import {POS_ORDERS_QUERY_KEY} from "@/context/pos/PosProvider";
import {getInputValidationProps} from "@/data/Validation";
import {notifyDanger, notifySuccess} from "@/utils/NotifyUtils";
import {api} from "@/data/api";

type Props = {
    show: boolean,
    setShow: (show: boolean) => void,
    order: PlacedOrderOverview,
};

const DEFAULT_VALUES: PosOrderCancellation = {
    reason: "",
    refunded: false,
};

/**
 * Cancel an order with a reason. When (part of) the order was paid online,
 * the employee can mark it as refunded right away (the refund itself is done
 * manually in Mollie).
 */
const CancelOrderModal = ({show, setShow, order}: Props) => {
    const intl = useIntl();
    const {notify} = useNotifications();
    const {dashboardLocation} = useRestaurant();
    const queryClient = useQueryClient();
    const paidOnline: boolean = (order.amountPaidOnline ?? 0) > 0;

    const {register, handleSubmit, reset, formState: {errors}} = useForm<PosOrderCancellation>({
        defaultValues: DEFAULT_VALUES,
    });

    const {mutate: cancel, isPending} = useMutation({
        mutationFn: (cancellation: PosOrderCancellation) =>
            api.orders.cancelOrder(dashboardLocation.id, order.cartPublicId, cancellation),
        onSuccess: () => {
            notifySuccess(intl.formatMessage({id: "orderCancelled"}), notify, TrashIcon);
            void queryClient.invalidateQueries({
                queryKey: [POS_ORDER_QUERY_KEY, dashboardLocation.id, order.cartPublicId],
            });
            void queryClient.invalidateQueries({queryKey: [POS_ORDERS_QUERY_KEY]});
            setShow(false);
        },
        onError: () => notifyDanger(intl.formatMessage({id: "cancelOrderFailed"}), notify),
    });

    // Start empty every time the modal opens
    useEffect(() => {
        if (show) reset(DEFAULT_VALUES);
    }, [show, reset]);

    const onSubmit = handleSubmit((values: PosOrderCancellation) => cancel({
        reason: values.reason.trim(),
        refunded: paidOnline && values.refunded,
    }));

    return (
        <Modal
            show={show}
            onClose={() => setShow(false)}
            title={intl.formatMessage({id: "cancelOrder"})}
            closeButtonTitle={intl.formatMessage({id: "back"})}
            disabled={isPending}
            loading={isPending}
            position={"center-bottom"}
            breakpoints={{md: {position: "center"}}}
            actionRow={
                <ActionRow>
                    <ActionRowButton
                        color={"danger"}
                        loading={isPending}
                        disabled={isPending}
                        buttonAction={{
                            as: "button",
                            actionProps: {
                                onClick: e => {
                                    e.preventDefault();
                                    void onSubmit();
                                },
                            },
                        }}
                    >
                        <FormattedMessage id={"cancelOrder"}/>
                    </ActionRowButton>
                </ActionRow>
            }
        >
            <form className={"flex flex-col gap-4 mt-4"} onSubmit={onSubmit}>
                <p>
                    <FormattedMessage id={"cancelOrderDescription"}/>
                </p>

                <TextareaField
                    rows={3}
                    autoFocus={true}
                    label={
                        <InputLabel>
                            <FormattedMessage id={"cancellationReason"}/>
                        </InputLabel>
                    }
                    placeholder={intl.formatMessage({id: "cancellationReasonPlaceholder"})}
                    disabled={isPending}
                    error={errors.reason?.message}
                    {...register("reason", {
                        ...getInputValidationProps(orderCancellationValidation.reason, intl),
                        // Only spaces is not a reason
                        validate: value => value.trim().length > 0
                            || intl.formatMessage({id: "enter-required-field"}),
                    })}
                />

                {
                    paidOnline &&
                    <>
                        <Alert
                            type={"warning"}
                            title={<FormattedMessage id={"cancelPaidOnlineTitle"}/>}
                            content={<FormattedMessage id={"cancelPaidOnlineDescription"}/>}
                        />
                        <Toggle
                            type={"checkbox"}
                            label={toggleLabel(intl.formatMessage({id: "alreadyRefunded"}))}
                            disabled={isPending}
                            {...register("refunded")}
                        />
                    </>
                }
            </form>
        </Modal>
    );
};

export default CancelOrderModal;